<script>
  import { Button, Image } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { Loader } from '@client/components/loader';

  /**
   * @typedef {Object} ImageAsset
   * @property {string} [imageurl] - URL of the image.
   * @property {Object} [metadata] - Metadata of the image.
   * @property {Object} [metadata.imageinfo] - Image information.
   * @property {number} [metadata.imageinfo.width] - Width of the image.
   * @property {number} [metadata.imageinfo.height] - Height of the image.
   */

  /**
   * Flag indicating if an image URL is invalid.
   * @type {boolean}
   */
  export let invalid = false;

  /**
   * Flag indicating if an image is currently uploading.
   * @type {boolean}
   */
  export let isUploading = false;

  /**
   * The currently image asset to preview.
   * @type {ImageAsset | undefined}
   */
  export let image;

  /**
   * Flag indicating if an image has been uploaded.
   * @type {boolean}
   */
  export let uploaded = false;

  /**
   * The URL of an image, if one is provided directly.
   * @type {string | undefined}
   */
  export let url;

  export let onRemove = () => {};

  /**
   * Generates a string representing the dimensions of an image.
   * @param {ImageAsset} image - The image asset.
   * @returns {string} A string in the format "width x height" or an empty string if dimensions are not available.
   */
  const dimensions = (image) => `${image?.metadata?.imageinfo?.width}x${image?.metadata?.imageinfo?.height}`;
</script>

<div class="preview border p-2 rounded" class:border-danger={invalid}>
  {#if isUploading}
    <Loader show={isUploading} />
  {/if}
  {#if image}
    {#if uploaded}
      <div class="upload-complete">
        <Icon name="check" class="upload-complete-icon" />
      </div>
    {/if}
    <small class="image-info-top">
      <span class="text-truncate">{image.filename}</span>
      <Button color="link" class="text-danger p-0" size="sm" on:click={onRemove}>
        <Icon name="trash-2" />
      </Button>
    </small>

    <div class="image-container">
      <Image
        alt="Preview"
        fluid
        src={image.imageurl}
        style="max-height: 100px"
        on:error={() => (invalid = true)}
        on:load={() => (invalid = false)}
      />
    </div>

    <small class="image-info-bottom">
      <span>{image.metadata.mimetype}</span>
      <span>{dimensions(image)}</span>
    </small>
  {:else if url}
    <Loader show={isUploading} />

    <div class="image-container">
      <Image
        alt="Preview"
        fluid
        src={url}
        style="max-height: 100px"
        on:error={() => (invalid = true)}
        on:load={() => (invalid = false)}
      />
    </div>
  {/if}
</div>

<style lang="scss" src="./logo-preview.scss"></style>
